import { SVGIcon } from '@superops/super-icons';
import { Avatar, TextStyle } from '@superops/ui-design-system';
import React, { ReactNode } from 'react';
import { DateTimeFormatter, getTranslatedText } from '@nile/components';
import {
  AuditAttribute,
  AuditData,
  AuditEntityAttribute,
  AuditMultiValuedFieldProps,
  AuditAttributesHandlerProps
} from '@nile/types';
import { FormattedMessage, useIntl } from 'react-intl';

const userFields = ['additionalRequester', 'requester', 'technician'];

const dateTypeMap = {
  datetime: 'toTechTimeZone'
};

function MultiValuedFields({
  entityName,
  label,
  multiValuedField,
  StrikeThrough,
  entityAttributesMap
}: AuditMultiValuedFieldProps): ReactNode {
  const entity: AuditEntityAttribute = entityAttributesMap[entityName];
  const isUser = userFields.includes(entityName);
  const isDateField = entity?.type === 'date' || entity?.type === 'datetime';

  return (
    <div className="fx actr g10 wrap line-break-anywhere">
      <FormattedMessage id={label} /> :
      {multiValuedField?.map((value) => {
        if (typeof value === 'string') {
          return (
            <span className="fx actr g10 wrap line-break-anywhere">
              {isUser && (
                <Avatar name={value} initials={value[0]} size="extraSmall" />
              )}

              <span className={StrikeThrough ? 'strike-through opacity5' : ''}>
                <TextStyle variation={!StrikeThrough && 'strong'}>
                  {isDateField ? (
                    <DateTimeFormatter
                      date={value}
                      type={dateTypeMap[entity?.type] || entity?.type}
                    />
                  ) : (
                    value
                  )}
                </TextStyle>
              </span>
            </span>
          );
        }
        return (
          <AttributesHandler
            skipName
            strikeThrough={StrikeThrough}
            showInitial={!!isUser}
            attributes={value?.attributes}
            entityAttributesMap={entityAttributesMap}
          />
        );
      })}
    </div>
  );
}

function checkAddedOrRemoved(attribute: AuditAttribute): boolean {
  const { value } = attribute;
  return 'added' in value || 'removed' in value;
}

function AttributesHandler({
  skipName,
  showInitial,
  attributes,
  entityAttributesMap,
  strikeThrough,
  entityName
}: AuditAttributesHandlerProps): React.ReactElement[] {
  const intl = useIntl();

  return attributes?.map((attr: AuditAttribute) => {
    const entity: AuditEntityAttribute = entityAttributesMap?.[attr?.name];
    const isUser = userFields.includes(attr?.name);
    const isDateField = entity?.type === 'date' || entity?.type === 'datetime';
    if (attr?.value && checkAddedOrRemoved(attr)) {
      return (
        <div className="fx col wrap line-break-anywhere">
          <TextStyle captalize variation="subdued">
            {getTranslatedText(intl, entity?.label || attr?.name)}
          </TextStyle>
          {attr.value?.removed?.length && (
            <MultiValuedFields
              StrikeThrough
              entityName={attr?.name}
              label="app.common.deleted"
              multiValuedField={attr.value.removed}
              entityAttributesMap={entityAttributesMap}
            />
          )}
          {attr.value?.added?.length && (
            <MultiValuedFields
              label="app.common.added"
              entityName={attr?.name}
              StrikeThrough={strikeThrough}
              multiValuedField={attr.value.added}
              entityAttributesMap={entityAttributesMap}
            />
          )}
        </div>
      );
    }

    if (
      typeof attr.value?.old === 'object' &&
      (attr.value?.old?.attributes || attr.value?.old?.value)
    ) {
      if (
        typeof attr.value?.new === 'object' &&
        (attr.value?.new?.attributes || attr.value?.new?.value)
      ) {
        return (
          <div className="fx col wrap line-break-anywhere">
            <TextStyle captalize variation="subdued">
              {getTranslatedText(intl, entity?.label || attr?.name)}
            </TextStyle>
            <div className="fx actr g10 wrap line-break-anywhere">
              <AttributesHandler
                skipName
                // showInitial={false}
                showInitial={!!isUser}
                attributes={
                  attr.value?.old?.value
                    ? [attr.value?.old]
                    : attr.value?.old?.attributes
                }
                strikeThrough
              />
              <SVGIcon
                name="RightArrow"
                size="xs"
                color="var(--sops-black-white-bg)"
              />
              <AttributesHandler
                skipName
                // showInitial={false}
                showInitial={!!isUser}
                attributes={
                  attr.value?.new?.value
                    ? [attr.value?.new]
                    : attr.value?.new?.attributes
                }
              />
            </div>
          </div>
        );
      }

      return (
        <div className="fx col wrap line-break-anywhere">
          <TextStyle captalize variation="subdued">
            {getTranslatedText(intl, entity?.label || attr?.name)}
          </TextStyle>
          <div className="fx actr g10 wrap line-break-anywhere">
            <AttributesHandler
              skipName
              // showInitial={false}
              showInitial={!!isUser}
              attributes={
                attr.value?.old?.value
                  ? [attr.value?.old]
                  : attr.value?.old?.attributes
              }
              strikeThrough
            />
            <SVGIcon
              name="RightArrow"
              size="xs"
              color="var(--sops-black-white-bg)"
            />
            <TextStyle variation="subdued">NA</TextStyle>
          </div>
        </div>
      );
    }

    if (
      typeof attr.value?.new === 'object' &&
      (attr.value?.new?.attributes || attr.value?.new?.value)
    ) {
      return (
        <div className="fx col wrap line-break-anywhere">
          <TextStyle captalize variation="subdued">
            {getTranslatedText(intl, entity?.label || attr?.name)}
          </TextStyle>
          <AttributesHandler
            skipName
            // showInitial={false}
            showInitial={!!isUser}
            attributes={
              attr.value?.new?.value
                ? [attr.value?.new]
                : attr.value?.new?.attributes
            }
          />
        </div>
      );
    }

    return (
      <div className="fx col wrap line-break-anywhere">
        {!skipName && (
          <TextStyle captalize variation="subdued">
            {getTranslatedText(intl, entity?.label || attr?.name)}
          </TextStyle>
        )}
        {attr?.value && (
          <div className="fx actr g10 wrap line-break-anywhere">
            {attr?.value?.old && (
              <span className="fx actr g10 wrap line-break-anywhere">
                {showInitial && (
                  <Avatar
                    name={attr.value?.old.toString()}
                    initials={attr.value?.old[0]}
                    size="extraSmall"
                  />
                )}
                <span className="strike-through opacity5">
                  <TextStyle>
                    {isDateField ? (
                      <DateTimeFormatter
                        date={attr.value?.old}
                        type={dateTypeMap[entity?.type] || entity?.type}
                      />
                    ) : (
                      <FormattedMessage
                        id={
                          entity?.type === 'enumeration'
                            ? `${entity?.entityType}.${attr.value?.old}`
                            : attr.value?.old.toString()
                        }
                      />
                    )}
                  </TextStyle>
                </span>
                <SVGIcon
                  name="RightArrow"
                  size="xs"
                  color="var(--sops-black-white-bg)"
                />
              </span>
            )}
            {attr.value?.new ? (
              <div className="fx actr g5 wrap line-break-anywhere">
                {showInitial && (
                  <Avatar
                    name={attr.value?.new.toString()}
                    initials={attr.value?.new[0]}
                    size="extraSmall"
                  />
                )}
                <span
                  className={strikeThrough ? 'strike-through opacity5' : ''}
                >
                  <TextStyle variation={!strikeThrough && 'strong'}>
                    {isDateField ? (
                      <DateTimeFormatter
                        date={attr.value?.new}
                        type={dateTypeMap[entity?.type] || entity?.type}
                      />
                    ) : (
                      <>
                        {entityName === 'ConversationThread' ? (
                          <TextStyle>
                            {attr.value?.new
                              ?.toString()
                              ?.split('_')
                              ?.join(' ')}
                          </TextStyle>
                        ) : (
                          <FormattedMessage
                            id={
                              entity?.type === 'enumeration'
                                ? `${entity?.entityType}.${attr.value?.new}`
                                : attr.value?.new.toString()
                            }
                          />
                        )}
                      </>
                    )}
                  </TextStyle>
                </span>
              </div>
            ) : (
              <TextStyle>NA</TextStyle>
            )}
          </div>
        )}
      </div>
    );
  });
}

export default AttributesHandler;
